
import { memo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Memoize the component to prevent unnecessary re-renders
const HeroSection = memo(() => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Set loaded state after component mounts to ensure animations work properly
    setIsLoaded(true);
  }, []);

  // Optimized animation variants
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1] // Use optimized bezier curve for smoother animation
      }
    }
  };

  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, // Changed from 0.1 to 1 to ensure full control via className opacity
      transition: { duration: 1.5 } 
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4, 
        delay: 0.2 
      }
    },
    hover: { 
      scale: 1.05,
      transition: { 
        type: "spring", 
        stiffness: 400, 
        damping: 10 
      } 
    }
  };

  return (
    <section className="min-h-screen flex flex-col md:flex-row">
      {/* Online Side - Entire section is now clickable */}
      <Link to="/online" className="flex-1 bg-black flex flex-col items-center justify-center p-8 md:p-12 transition-all duration-500 hover:flex-[1.1] relative overflow-hidden group">
        {/* Background with direct inline style to ensure opacity works */}
        <div 
          className="absolute inset-0 bg-[url('/lovable-uploads/4c1f05f0-3d99-4e61-86c2-9fcd077ecb6a.webp')] bg-cover bg-center z-0 transition-all duration-500 group-hover:brightness-125"
          style={{ opacity: isLoaded ? 0.5 : 0, transition: "opacity 0.5s ease" }}
        ></div>
        {/* Gradient overlay with reduced darkness */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 z-0 transition-all duration-500"></div>
        
        {/* Hover layer with higher opacity */}
        <div 
          className="absolute inset-0 bg-[url('/lovable-uploads/4c1f05f0-3d99-4e61-86c2-9fcd077ecb6a.webp')] bg-cover bg-center z-0 opacity-0 group-hover:opacity-75 transition-opacity duration-500"
        ></div>
        
        <div className="relative z-10 max-w-md mx-auto text-center">
          <motion.div
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={textVariants}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">{t('hero.online.title')}</h2>
            <p className="text-muted-foreground mb-8">
              {t('hero.online.description')}
            </p>
            <motion.span 
              variants={buttonVariants}
              whileHover="hover"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-anzir px-6 py-3 font-medium text-white transition-all hover:bg-anzir/90 focus-ring"
            >
              {t('hero.online.cta')}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </motion.span>
          </motion.div>
        </div>
      </Link>

      {/* Offline Side - Entire section is now clickable */}
      <Link to="/offline" className="flex-1 bg-black flex flex-col items-center justify-center p-8 md:p-12 transition-all duration-500 hover:flex-[1.1] relative overflow-hidden group">
        {/* Background with direct inline style to ensure opacity works */}
        <div
          className="absolute inset-0 bg-[url('/lovable-uploads/1f43e10a-f320-4481-ad5c-4b7ed667bd0f.webp')] bg-cover bg-center z-0 transition-all duration-500 group-hover:brightness-125"
          style={{ opacity: isLoaded ? 0.5 : 0, transition: "opacity 0.5s ease" }}
        ></div>
        {/* Gradient overlay with reduced darkness */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 z-0 transition-all duration-500"></div>

        {/* Hover layer with higher opacity */}
        <div 
          className="absolute inset-0 bg-[url('/lovable-uploads/1f43e10a-f320-4481-ad5c-4b7ed667bd0f.webp')] bg-cover bg-center z-0 opacity-0 group-hover:opacity-75 transition-opacity duration-500"
        ></div>
        
        <div className="relative z-10 max-w-md mx-auto text-center">
          <motion.div
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={textVariants}
            transition={{ delay: 0.1 }} // Stagger the animation slightly
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">{t('hero.offline.title')}</h2>
            <p className="text-muted-foreground mb-8">
              {t('hero.offline.description')}
            </p>
            <motion.span 
              variants={buttonVariants}
              whileHover="hover"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-white px-6 py-3 font-medium text-black transition-all hover:bg-white/90 focus-ring"
            >
              {t('hero.offline.cta')}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </motion.span>
          </motion.div>
        </div>
      </Link>
    </section>
  );
});

// Display name for debugging
HeroSection.displayName = "HeroSection";

export default HeroSection;
